import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';

function Brand() {
  const { brand } = useParams();
  const { darkMode } = useTheme();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const decodedBrand = decodeURIComponent(brand);

  useEffect(() => {
    api('/api/articles').then(res => res.json()).then(data => {
      const filtered = data
        .filter(a => a.published && a.tags?.some(t => t.toLowerCase() === decodedBrand.toLowerCase()))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt) || b.id - a.id);
      setArticles(filtered);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [decodedBrand]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const cardBg = darkMode ? 'bg-slate-900 border-slate-800 hover:border-slate-700' : 'bg-white border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md';
  const titleColor = darkMode ? 'text-white group-hover:text-teal-400' : 'text-stone-900 group-hover:text-teal-600';
  const metaColor = darkMode ? 'text-gray-500' : 'text-stone-400';
  const excerptColor = darkMode ? 'text-gray-500' : 'text-stone-500';

  return (
    <div className={`min-h-screen pt-24 sm:pt-28 pb-16 px-4 transition-colors duration-200 ${darkMode ? '' : 'bg-stone-50'}`}>
      <SEO
        title={`${decodedBrand} - Artikler og tester`}
        path={`/merke/${brand}`}
        description={`Les alle artikler og tester om ${decodedBrand} elbiler. Rekkeviddetester, kjøretester og nyheter fra Effekt.`}
      />

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link to="/artikler" className={`text-sm mb-3 inline-block ${darkMode ? 'text-gray-400 hover:text-teal-400' : 'text-stone-400 hover:text-teal-600'}`}>
            ← Alle artikler
          </Link>
          <h1 className={`text-4xl sm:text-5xl font-bold ${darkMode ? 'text-white' : 'text-stone-900'}`} style={{ fontFamily: "'Lora', serif" }}>
            {decodedBrand}
          </h1>
          <p className={`text-lg mt-2 ${metaColor}`} style={{ fontFamily: "'Lora', serif" }}>
            {articles.length} {articles.length === 1 ? 'artikkel' : 'artikler'}
          </p>
        </div>

        {loading && (
          <div className="space-y-6">
            {[1,2,3].map(i => (
              <div key={i} className={`rounded-2xl p-6 border animate-pulse ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-stone-200 border-stone-200'}`}>
                <div className={`h-48 rounded-xl mb-4 ${darkMode ? 'bg-slate-700' : 'bg-stone-300'}`} />
                <div className={`h-6 w-2/3 rounded mb-2 ${darkMode ? 'bg-slate-700' : 'bg-stone-300'}`} />
                <div className={`h-4 w-full rounded ${darkMode ? 'bg-slate-700' : 'bg-stone-300'}`} />
              </div>
            ))}
          </div>
        )}

        {!loading && (
          <div className="space-y-6">
            {articles.map((article, index) => (
              <Link
                key={article.id}
                to={`/artikler/${article.slug}`}
                className="block group"
                style={{ animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both` }}
              >
                <article className={`border rounded-2xl overflow-hidden transition-all duration-150 ${cardBg}`}>
                  {article.coverImage && (
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-48 sm:h-56 object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="p-6">
                    <h2 className={`text-xl sm:text-2xl font-bold mb-2 transition-colors duration-150 ${titleColor}`} style={{ fontFamily: "'Lora', serif" }}>
                      {article.title}
                    </h2>
                    <p className={`mb-3 leading-relaxed ${excerptColor}`} style={{ fontFamily: "'Lora', serif" }}>
                      {article.excerpt}
                    </p>
                    <div className={`flex items-center justify-between text-sm ${metaColor}`}>
                      <span>{article.author}</span>
                      <time dateTime={article.createdAt}>{formatDate(article.createdAt)}</time>
                    </div>
                  </div>
                </article>
              </Link>
            ))}

            {articles.length === 0 && (
              <p className={`text-center py-16 text-lg ${metaColor}`}>Ingen artikler funnet for {decodedBrand}.</p>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default Brand;
